import { memo, useEffect, useState } from 'react';
import { subscriptionAPI } from '../../../../../services/redwolfSdkAPI';
import { format } from 'date-fns';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import Pagination from '../../../../common/components/Pagination';
import { Link } from 'react-router-dom';
import lodash from 'lodash';
import Select from 'react-select';
import DropdownIndicator from '../../../../common/components/DropdownIndicator';
import { currencySymbol, dateFormat, reactSelectStyles } from '../../../../../utils/constants';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';
import TableSkeleton from '../../../../common/skeletons/TableSkeleton';

interface PurchaseHistoryProps {
	updatePurchaseHistory?: any;
}

const PurchaseHistory = ({ updatePurchaseHistory }: PurchaseHistoryProps) => {
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(true);
	const [purchaseHistoryList, setpurchaseHistoryList]: any = useState([]);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [requestRedwolfService] = useRequestRedwolfService();
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Purchase History' });

	const purchaseHistoryDropdownOptions = [
		{
			label: 'Newest',
			value: 'newest'
		},
		{
			label: 'Oldest',
			value: 'oldest'
		}
	];

	const onDropdownKeyDown = (event: any) => {
		if (event.code === 'Enter') {
			setIsDropdownOpen(!isDropdownOpen);
		}
	};

	const onDropdownMenuClose = () => {
		setIsDropdownOpen(false);
	};

	const onDropdownMenuOpen = () => {
		setIsDropdownOpen(true);
	};

	useEffect(() => {
		getPurchaseHistoryList();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatePurchaseHistory]);

	const getPurchaseHistoryList = () => {
		requestRedwolfService(subscriptionAPI.getV2Invoices(token))
			.then((sdkResponse: any) => {
				setIsSkeletonLoading(false);

				if (
					sdkResponse.constructor.$metadata$.simpleName === 'Success' &&
					sdkResponse.data.invoiceData &&
					sdkResponse.data.invoiceData.toArray().length
				) {
					sortPurchaseHistory(sdkResponse.data.invoiceData.toArray(), 'desc');
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSkeletonLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const sortPurchaseHistory = (purchaseHistory: any, sortBy: any) => {
		setpurchaseHistoryList(
			lodash.orderBy(purchaseHistory, ['billingDate', 'plan.appName'], [sortBy, 'asc'])
		);
	};

	const handleSorting = (event: any) => {
		if (event.value === 'newest') {
			sortPurchaseHistory(purchaseHistoryList, 'desc');
		} else {
			sortPurchaseHistory(purchaseHistoryList, 'asc');
		}

		setIsDropdownOpen(false);
		setCurrentPageNumber(1);
	};

	const handlePagination = (currentPage: any) => {
		setCurrentPageNumber(parseInt(currentPage));
	};

	const paginationPerPage = 5;
	const indexOfLastItem = currentPageNumber * paginationPerPage;
	const indexOfFirstItem = indexOfLastItem - paginationPerPage;

	const currentPurchaseOrdersList = purchaseHistoryList.slice(indexOfFirstItem, indexOfLastItem);

	return (
		<div className="sb-pr-component sub-purchase-comp purchase-history-container">
			<div className="subs-head">
				<h2 id="purchase-history-title" className="ac-title">
					Purchase History
				</h2>

				{currentPurchaseOrdersList.length > 0 && (
					<div className="subs-dropdown" id="empty-purchase-dropdown">
						<div className="select-field">
							<Select
								defaultValue={purchaseHistoryDropdownOptions[0]}
								isSearchable={false}
								styles={reactSelectStyles}
								onMenuOpen={onDropdownMenuOpen}
								onMenuClose={onDropdownMenuClose}
								menuIsOpen={isDropdownOpen}
								onKeyDown={onDropdownKeyDown}
								options={purchaseHistoryDropdownOptions}
								onChange={handleSorting}
								aria-labelledby="purchase-history-title"
								components={{ IndicatorSeparator: () => null, DropdownIndicator }}
							/>
						</div>
					</div>
				)}
			</div>

			{isSkeletonLoading ? (
				<TableSkeleton />
			) : (
				currentPurchaseOrdersList.length === 0 && (
					<p className="empty-title">You do not have any purchase history.</p>
				)
			)}

			{currentPurchaseOrdersList.length > 0 && (
				<div className="subs-table">
					<table className="table subscrip-table purchase-history">
						<thead>
							<tr>
								<th scope="col">App & Title</th>
								<th scope="col">Payment</th>
								<th scope="colgroup" colSpan={2}>
									Price & Billing Date
								</th>
							</tr>
						</thead>

						<tbody>
							{currentPurchaseOrdersList.map((card: any, index: number) => {
								const addOns: any = card.detailsLine2.split(':');
								return (
									<tr key={index}>
										<td className="app-td">
											<div className="icon-title">
												<div className="sapp-icon">
													<img
														src={card.appImageUrl}
														className="plan-app-icon"
														alt="Purchased order"
													/>
												</div>
												<div className="sapp-title">
													<h3 id={`purchaseappname-${index}`} className="bill-price">
														{card.appName}
													</h3>

													<p className="date">{card.description}</p>
													<p className="purchase-addon">{addOns.length > 0 ? addOns[0] : ''}</p>
													<span className="mob sapp-price-date">
														<span className="bill-price">
															{currencySymbol + Number(card.total).toFixed(2)}
														</span>
														<span className="date">
															{format(new Date(card.billingDate), dateFormat)}
														</span>
													</span>
												</div>
											</div>
										</td>
										<td className="app-td desk">
											<span className="card-number">
												{card.cardTypeCode}...{card.cardLastFour}
											</span>
										</td>
										<td className="app-td desk">
											<span className="bill-price">
												{currencySymbol + Number(card.total).toFixed(2)}
											</span>
											<span className="date">{format(new Date(card.billingDate), dateFormat)}</span>
										</td>
										<td className="app-td">
											<span className="update-link">
												<Link
													to={{
														pathname: '/my-invoice',
														search: `?invoiceid=${card.invoiceId}`
													}}
													target="_blank"
													className="link"
													aria-describedby={`purchaseappname-${index}`}>
													View Receipt
												</Link>
											</span>
											<div className="hidetext">opens in new tab</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>

					{purchaseHistoryList.length > paginationPerPage && (
						<div className="table-navigation" id="purchasePaginationAppend">
							<Pagination
								totalNumberOfPages={purchaseHistoryList.length}
								currentPage={currentPageNumber}
								paginationPerPage={5}
								onPaginationChange={handlePagination}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default memo(PurchaseHistory);
